/**
 * Lightweight date helpers replacing moment.js.
 * Uses native Date + Intl.DateTimeFormat.
 *
 * DUPLICATED, BYTE-FOR-BYTE, at `site/app/utils/dateHelpers.ts` and
 * `app/app/utils/dateHelpers.ts`. It is framework-free with no dependencies, so it belongs in
 * `shared/` — but `shared/src/utils/date.ts` is still a placeholder and neither frontend
 * depends on `@anddine/shared` yet, so until that is wired up EVERY change lands in both
 * copies in the SAME PR, comments included (CLAUDE.md, site rules).
 *
 * They had already drifted once: app kept `formatDate`'s token-by-token loop long after site
 * replaced it with the single-pass version, so `formatDate(dec17, "MMMM")` still rendered
 * "17ecember" on the app's Premium calendar header and cut-off strings. That is what a
 * "harmless" divergence looks like — the same call, two answers, no error either side.
 */

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

// Exported for the string formatters below, which build their own dates rather than
// going through `formatDate`'s token map. Kept as the single implementation — a second
// copy is how the two families would drift on the 11th/12th/13th.
export function ordinal(n: number): string {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

export function formatDate(date: Date, format: string): string {
  if (!date || isNaN(date.getTime())) return "";
  const map: Record<string, string> = {
    YYYY: String(date.getFullYear()),
    yyyy: String(date.getFullYear()),
    MM: pad(date.getMonth() + 1),
    DD: pad(date.getDate()),
    D: String(date.getDate()),
    Do: ordinal(date.getDate()),
    dddd: DAYS[date.getDay()],
    ddd: DAYS[date.getDay()].slice(0, 3),
    MMMM: MONTHS[date.getMonth()],
    MMM: MONTHS[date.getMonth()].slice(0, 3),
    HH: pad(date.getHours()),
    mm: pad(date.getMinutes()),
    ss: pad(date.getSeconds()),
  };

  // Single pass over the format string, longest token first so MMMM wins over
  // MM. This MUST stay a single `replace` — substituting token-by-token rescans
  // values already written into the result, so the `D` pass chewed the capital
  // D out of a substituted month name: `formatDate(dec17, "MMMM")` rendered
  // "17ecember", and "ddd D MMM" rendered "Thu 17 17ec".
  const tokens = Object.keys(map).sort((a, b) => b.length - a.length);
  return format.replace(new RegExp(tokens.join("|"), "g"), (token) => map[token]);
}

export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function isBefore(a: Date, b: Date): boolean {
  return a.getTime() < b.getTime();
}

export function isBeforeDay(a: Date, b: Date): boolean {
  const ac = new Date(a.getFullYear(), a.getMonth(), a.getDate());
  const bc = new Date(b.getFullYear(), b.getMonth(), b.getDate());
  return ac.getTime() < bc.getTime();
}

export function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

export function subtractDays(date: Date, days: number): Date {
  return addDays(date, -days);
}

export function subtractHours(date: Date, hours: number): Date {
  const d = new Date(date);
  d.setHours(d.getHours() - hours);
  return d;
}

export function subtractMinutes(date: Date, minutes: number): Date {
  const d = new Date(date);
  d.setMinutes(d.getMinutes() - minutes);
  return d;
}

/**
 * ISO weekday: 1=Monday … 7=Sunday
 */
export function isoWeekday(date: Date): number {
  const day = date.getDay();
  return day === 0 ? 7 : day;
}

export function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function parseISO(str: string): Date {
  return new Date(str);
}

// ── Plain-string formatters for "YYYY-MM-DD HH:mm" API values ──
// These avoid timezone-sensitive Date display methods (toLocaleDateString etc.)

// Derived, not duplicated: formatDate's `ddd`/`MMM` tokens already slice DAYS
// and MONTHS at :56 and :58, and CLAUDE.md asks for that form. Two literal
// copies of the same abbreviations in one file drift.
const SHORT_DAYS = DAYS.map((day) => day.slice(0, 3));
const SHORT_MONTHS = MONTHS.map((month) => month.slice(0, 3));

/**
 * Weekday index for the date half of a wall-clock string, derived from the
 * sliced parts via Date.UTC.
 *
 * `new Date(raw).getDay()` cannot be used: for a date-only value the spec
 * parses "2026-03-31" as UTC midnight but getDay() reads local, so any viewer
 * behind UTC sees the previous day's name. Building and reading in UTC keeps
 * the answer the same everywhere.
 *
 * It also kept SSR (UTC on the box) and the browser from disagreeing, which
 * produced a hydration mismatch on a value the customer is asked to confirm.
 * Harmless in UTC/BST, which is why it survived; wrong for anyone travelling.
 *
 * Replaces weekdayIndexFromDateString, which main added with a byte-identical
 * body while this branch was open.
 */
function wallClockWeekday(raw: string): number {
  const year = parseInt(raw.slice(0, 4), 10);
  const monthIdx = parseInt(raw.slice(5, 7), 10) - 1;
  const day = parseInt(raw.slice(8, 10), 10);
  return new Date(Date.UTC(year, monthIdx, day)).getUTCDay();
}

/**
 * Format a plain "YYYY-MM-DD HH:mm" string as "Wed 31 Mar 2026".
 * Pure string slicing — no locale display methods, no timezone.
 */
export function formatDateStringLong(raw: string): string {
  const date = wallClockDate(raw);
  if (!date) return "";
  const year = date.slice(0, 4);
  const monthIdx = parseInt(date.slice(5, 7), 10) - 1;
  const day = parseInt(date.slice(8, 10), 10);
  const weekday = SHORT_DAYS[wallClockWeekday(date)];
  return `${weekday} ${day} ${SHORT_MONTHS[monthIdx]} ${year}`;
}

/**
 * Format a plain "YYYY-MM-DD HH:mm" string as "Wed 31 Mar" (no year).
 */
export function formatDateStringShort(raw: string): string {
  const date = wallClockDate(raw);
  if (!date) return "";
  const monthIdx = parseInt(date.slice(5, 7), 10) - 1;
  const day = parseInt(date.slice(8, 10), 10);
  const weekday = SHORT_DAYS[wallClockWeekday(date)];
  return `${weekday} ${day} ${SHORT_MONTHS[monthIdx]}`;
}

/**
 * Format a plain "YYYY-MM-DD HH:mm" string as "Wednesday 16th September" (no year).
 *
 * The long form the premium screens read out loud, where the date IS the thing being
 * confirmed and an abbreviation reads as a system string rather than a day. Sits in this
 * family, not `formatDate`, for the same reason its siblings do: it must not construct a
 * Date for anything the user sees, only for the weekday index.
 */
export function formatDateStringFull(raw: string): string {
  // Through wallClockDate like every sibling in this family: a bare `!raw` guard
  // left this the only one that would render a calendar-impossible value, so
  // "2026-13-45" printed "Sunday 45th undefined".
  const date = wallClockDate(raw);
  if (!date) return "";
  const monthIdx = parseInt(date.slice(5, 7), 10) - 1;
  const day = parseInt(date.slice(8, 10), 10);
  const weekday = DAYS[wallClockWeekday(date)];
  return `${weekday} ${ordinal(day)} ${MONTHS[monthIdx]}`;
}

/**
 * Reformat "YYYY-MM-DD ..." to "DD.MM.YYYY".
 */
export function formatDateStringDotted(raw: string): string {
  const date = wallClockDate(raw);
  if (!date) return "";
  return `${date.slice(8, 10)}.${date.slice(5, 7)}.${date.slice(0, 4)}`;
}

/**
 * The date half of a wall-clock string, "YYYY-MM-DD". "" when malformed.
 *
 * The shape check alone is not enough: "2026-13-45" is digits in the right
 * places, and every downstream Date constructor rolls the overflow over rather
 * than rejecting it — new Date(2026, 12, 45) is 14 Feb 2027. That is how
 * SHORT_MONTHS[12] came back undefined and the UI rendered
 * "Sun 45 undefined 2026". So the parts are round-tripped through Date.UTC and
 * a value that comes back as a different day is rejected.
 */
export function wallClockDate(raw?: string | null): string {
  if (!raw || typeof raw !== "string") return "";
  // The time component is fully range-checked here on purpose. If it is not,
  // an invalid time such as "99:99" still yields a valid date, and
  // wallClockToInstant then falls back to midnight — so a malformed cutoff
  // reads as a real past time and locks the order.
  if (!/^\d{4}-\d{2}-\d{2}(?:[ T](?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d(?:\.\d{1,3})?)?)?$/.test(raw))
    return "";
  const date = raw.slice(0, 10);
  const year = parseInt(date.slice(0, 4), 10);
  const monthIdx = parseInt(date.slice(5, 7), 10) - 1;
  const day = parseInt(date.slice(8, 10), 10);
  const probe = new Date(Date.UTC(year, monthIdx, day));
  const isRealDate =
    probe.getUTCFullYear() === year &&
    probe.getUTCMonth() === monthIdx &&
    probe.getUTCDate() === day;
  return isRealDate ? date : "";
}

/**
 * The time half of a wall-clock string, "HH:mm". "" when absent or malformed.
 *
 * Named for the call sites that were each doing `raw.slice(11, 16)` inline —
 * this is that slice, in one place, so the rule is greppable.
 */
export function formatWallClockTime(raw?: string | null): string {
  if (!raw || typeof raw !== "string") return "";
  if (!wallClockDate(raw)) return "";
  // End-anchored: as a prefix match this accepted "2026-10-25 14:30garbage"
  // and rendered it as a valid time. Optional seconds and milliseconds are the
  // legacy shapes that genuinely reach us; a trailing Z is NOT accepted any
  // more (e05f97a) — an offset-bearing value is an instant, not a wall clock,
  // and no live order carries one in these columns.
  if (!/^\d{4}-\d{2}-\d{2}[ T](?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d(?:\.\d{1,3})?)?$/.test(raw))
    return "";
  const time = raw.slice(11, 16);
  const [hours, minutes] = time.split(":").map(Number) as [number, number];
  return hours > 23 || minutes > 59 ? "" : time;
}

/**
 * Subtract minutes from "HH:mm" as string arithmetic, wrapping at midnight.
 * Mirrors the backend's subtractMinutesFromTime and the hub's copy.
 */
/** "H:mm" or "HH:mm[:ss]", end-anchored and range-checked. */
const CLOCK_TIME = /^(\d{1,2}):([0-5]\d)(?::[0-5]\d)?$/;

/** "9:30" → "09:30". "" when the value is not a usable time. */
function normalizeTimeToHHmm(time: string): string {
  const match = CLOCK_TIME.exec(String(time).trim());
  if (!match) return "";
  const hours = Number(match[1]);
  if (hours > 23) return "";
  return `${pad(hours)}:${match[2]}`;
}

export function subtractMinutesFromTime(time: string, mins: number): string {
  // The old pattern was a prefix match with no range check, so it was wrong in
  // both directions against the hub and the backend: "99:99" produced "04:09"
  // and "14:30garbage" produced "14:00", while a perfectly valid "9:30" was
  // rejected because it insisted on two digits. The backend's copy
  // (helpers/index.ts, #614) and the hub's both normalise then range-check.
  const normalised = normalizeTimeToHHmm(time);
  if (!normalised) return "";
  const offset = Number(mins);
  if (!Number.isFinite(offset)) return "";
  // ?? 0 on each: noUncheckedIndexedAccess is on, so destructuring a split()
  // gives number | undefined and the arithmetic below is two TS18048s. At runtime
  // normalizeTimeToHHmm has already guaranteed "HH:mm", so neither can be missing
  // — this states that invariant to the compiler rather than widening it away.
  const [h, m] = normalised.split(":").map(Number);
  const total = ((((h ?? 0) * 60 + (m ?? 0) - offset) % 1440) + 1440) % 1440;
  return `${pad(Math.floor(total / 60))}:${pad(total % 60)}`;
}

/**
 * The 30-minute arrival window ending at a wall-clock time:
 * "2026-10-25 14:30" → "14:00 - 14:30".
 */
export function formatWallClockWindow(raw?: string | null, mins = 30): string {
  const end = formatWallClockTime(raw);
  if (!end) return "";
  // Guard the start too, not just the end: a non-finite `mins` makes the
  // subtraction return "" and this would render " - 14:30". Same shape as the
  // "NaN:NaN - " the backend was emitting into customer email (#614).
  const start = subtractMinutesFromTime(end, mins);
  if (!start) return "";
  return `${start} - ${end}`;
}

/**
 * The *date half only* of a wall-clock string, as a local Date, for feeding to
 * formatDate when a weekday or month name is wanted.
 *
 * Built at local midnight on purpose: formatDate reads its parts back with the
 * local getters, so constructing locally makes the two cancel out and the same
 * calendar date renders in every zone. The time half is deliberately excluded —
 * it belongs to formatWallClockTime, never to a Date.
 */
export function wallClockToLocalDate(raw?: string | null): Date | null {
  const date = wallClockDate(raw);
  if (!date) return null;
  const [year, month, day] = date.split("-").map(Number) as [number, number, number];
  return new Date(year, month - 1, day);
}

/**
 * The "dddd D MMMM" heading both order pages show for the selected filter date.
 *
 * Lived inline in /upcoming and /previous with the same comment copied into
 * both; only the fallback text differed. Shared so the two cannot drift, which
 * is how one of them kept an inline day/month array after the other dropped it.
 */
export function formatSelectedDateDisplay(
  raw: string | null | undefined,
  fallback: string,
): string {
  const date = wallClockToLocalDate(raw);
  return date ? formatDate(date, "dddd D MMMM") : fallback;
}

export const UK_TIMEZONE = "Europe/London";

const UK_PARTS = new Intl.DateTimeFormat("en-GB", {
  timeZone: UK_TIMEZONE,
  hour12: false,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});

// The only two offsets London uses. GMT is tried first so an ambiguous time
// inside the October fold resolves to the LATER occurrence — matching the
// backend's ukLocalToUtc (date-fns-tz zonedTimeToUtc), which is what books with
// PedalMe and is therefore the authority on which instant a wall-clock string
// denotes. Reordering this desynchronises the surfaces.
const UK_OFFSETS_MS = [0, 3_600_000];

function ukOffsetMs(instant: number): number {
  // Probe on a whole second. Intl resolves no finer, so a sub-second input would
  // have its milliseconds counted as part of the offset — and wallClockToInstant
  // applies this twice, so "14:30:45.250" came back as .750. Latent until the
  // instant path started carrying seconds. No UK offset has ever been sub-second.
  const whole = Math.floor(instant / 1000) * 1000;
  const parts = UK_PARTS.formatToParts(new Date(whole));
  const at = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  // Some engines render midnight as hour 24 under hour12:false while leaving the
  // date on the same day, which would put Date.UTC a full day out.
  const hour = at("hour") === 24 ? 0 : at("hour");
  const asUtc = Date.UTC(at("year"), at("month") - 1, at("day"), hour, at("minute"), at("second"));
  return asUtc - whole;
}

/**
 * The sub-minute half of a wall-clock string, as [seconds, milliseconds].
 *
 * formatWallClockTime trims to "HH:mm" on purpose — every display path wants
 * that. The arithmetic path must not: a cutoff stored as "2026-12-15 14:30:45"
 * treated as 14:30:00 made the countdown and the edit lock flip 45 seconds
 * early. The backend keeps these too (ukLocalToUtc), so the two agree.
 */
function wallClockSubMinute(raw?: string | null): [number, number] {
  if (!raw || typeof raw !== "string") return [0, 0];
  const match =
    /^\d{4}-\d{2}-\d{2}[ T](?:[01]\d|2[0-3]):[0-5]\d(?::([0-5]\d)(?:\.(\d{1,3}))?)?$/.exec(raw);
  if (!match) return [0, 0];
  return [
    match[1] === undefined ? 0 : Number(match[1]),
    match[2] === undefined ? 0 : Number(String(match[2]).padEnd(3, "0")),
  ];
}

/**
 * Shift a UK wall-clock string by whole minutes, staying in wall-clock space.
 *
 * Calendar arithmetic on the order's own clock: it must not pick up the viewer's
 * timezone or a DST jump, which is exactly what Date#setHours/#setDate do. The
 * internal Date is UTC-only and never read with local getters, so no offset can
 * leak in. Mirrors the hub's shiftWallClock — keep the two in step.
 */
export function shiftWallClock(raw?: string | null, mins = 0): string {
  const date = wallClockDate(raw);
  if (!date) return "";
  const offset = Number.isFinite(Number(mins)) ? Number(mins) : 0;
  const time = formatWallClockTime(raw);
  const [year, month, day] = date.split("-").map(Number) as [number, number, number];
  const [hour, minute] = time ? (time.split(":").map(Number) as [number, number]) : [0, 0];
  const shifted = new Date(Date.UTC(year, month - 1, day, hour, minute) + offset * 60_000);
  const pad = (n: number) => String(n).padStart(2, "0");
  const nextDate = `${shifted.getUTCFullYear()}-${pad(shifted.getUTCMonth() + 1)}-${pad(shifted.getUTCDate())}`;
  return time
    ? `${nextDate} ${pad(shifted.getUTCHours())}:${pad(shifted.getUTCMinutes())}`
    : nextDate;
}

/**
 * The real instant a UK wall-clock string denotes, resolved against
 * Europe/London — BST or GMT according to the date, read from the platform's
 * own tz database via Intl.
 *
 * Only for comparing against now ("has the cutoff passed?"). Never for display:
 * rendering an instant puts the viewer's timezone back into the answer.
 */
export function wallClockToInstant(raw?: string | null): Date | null {
  // No offset-bearing branch, on purpose. These columns are UK wall-clock
  // STRING(20); Darko checked the live dump and not one of 16,831 orders carries
  // a T or Z in any of them. While the branch existed, a trailing Z was sliced as
  // wall clock for display but honoured as an instant here, so the same string
  // read 09:05 on screen and locked at 10:05 in BST — and the hub resolved it the
  // other way again. Rejecting it outright removes the divergence without anyone
  // having to pick a policy for data that does not exist. If such a value ever
  // does arrive this returns null, and every caller treats that as "no cutoff" —
  // the permissive direction, so nobody is locked out of an editable order.
  const date = wallClockDate(raw);
  if (!date) return null;
  const time = formatWallClockTime(raw);
  const [year, month, day] = date.split("-").map(Number) as [number, number, number];
  const [hour, minute] = time ? (time.split(":").map(Number) as [number, number]) : [0, 0];
  const [seconds, millis] = wallClockSubMinute(raw);
  const naive = Date.UTC(year, month - 1, day, hour, minute, seconds, millis);

  // The UK has exactly two offsets, so try both and keep the candidate that
  // agrees with itself — the instant whose London offset is the one used to
  // build it. Order matters only for an ambiguous time; see UK_OFFSETS_MS.
  // Must stay identical to the hub's wallClockToInstant for every shape that
  // actually reaches us — the two are asserted against the same fixtures below.
  //
  // One deliberate exception, and it is a divergence rather than an oversight:
  // this rejects a trailing Z where the hub and the backend accept it. Darko
  // checked the live dump and not one of 16,831 orders carries a T or Z in any
  // of these columns, and the API never emits one for a datetime column — the
  // DB layer returns the raw UTC text. Accepting it means choosing a reading for
  // data that does not exist, which is how the backend ended up with two
  // contradictory readings in one file (ukLocalToUtc treats a trailing Z as UK
  // wall clock; isDateAvailableForMaker treats the same value as a real
  // instant). Rejecting needs no policy at all. See the Z case in the shared
  // fixture block.
  // against the same fixtures.
  for (const offset of UK_OFFSETS_MS) {
    const candidate = naive - offset;
    if (ukOffsetMs(candidate) === offset) return new Date(candidate);
  }

  // Neither agrees only inside the March gap, where the wall-clock time never
  // happens. Fall back to a refinement pass rather than returning null.
  return new Date(naive - ukOffsetMs(naive - ukOffsetMs(naive)));
}
